import React from 'react';
import linkifyStr from 'linkifyjs/string';
import { fromGlobalId } from 'graphql-relay';

import map from 'lib/react/html/map';
import Styled from 'styled';
import EmojiOneView from 'lib/emojione/emojione-view';
import RouteLinkView from 'lib/ui/route-link-view';
import escapeHtml from 'lib/html/escape-html';

import linkifyMentions from './transformations/linkify-mentions';
import linkifyHashtags from './transformations/linkify-hashtags';

import defaultStyles from './enhanced-text.scss';
import emojiOneStyles from './emojione-view.scss';

const YOUTUBE_REGEX = /(http:|https:|)\/\/(player.|www.)?(youtu(be\.com|\.be|be\.googleapis\.com))\/(video\/|embed\/|watch\?v=|v\/)?([A-Za-z0-9._%-]*)(&\S+)?/;

export interface Props {
    children: React.ReactNode;
    styles: typeof defaultStyles;
    groupId?: string | null;
    followLink?: boolean;
    hashtags?: boolean;
}

class EnhancedText extends React.Component<Props> {
    embededVideoHref?: string;

    onElement = (name: string, props: any, children: React.ReactNode) => {
        const { styles, followLink = false } = this.props;
        const { embededVideoHref } = this;

        if (name === 'a') {
            const iframe = this._createYoutubeVideoIframe(props.href);

            if (iframe && (!embededVideoHref || embededVideoHref === props.href)) {
                this.embededVideoHref = props.href;
                return iframe;
            }

            return (
                <RouteLinkView {...props} follow={Boolean(followLink)} className={styles.link}>
                    {children}
                </RouteLinkView>
            );
        }

        return undefined;
    };

    onText = (text: string) => {
        const input = this._replaceSpacesWithNbsp(text);

        return (
            <EmojiOneView
                options={{
                    ascii: true,
                }}
                styles={emojiOneStyles}
                pngToSvg={true}
            >
                {input}
            </EmojiOneView>
        );
    };

    _createYoutubeVideoIframe = (url: string) => {
        const { styles } = this.props;
        const youtubeID = this._extractYoutubeVideoID(url);
        if (youtubeID) {
            return (
                <span className={styles.youtubeContainer}>
                    <iframe
                        title={`youtube_${youtubeID}`}
                        src={`//www.youtube.com/embed/${youtubeID}`}
                        className={styles.youtubeIframe}
                        frameBorder={0}
                    />
                </span>
            );
        }
        return null;
    };

    _extractYoutubeVideoID = (url: string) => {
        url.match(YOUTUBE_REGEX);
        if (RegExp.$3.indexOf('youtu') > -1) {
            return RegExp.$6;
        }
    };

    _replaceSpacesWithNbsp(text: string) {
        return text.replace(/^ /, '&nbsp');
    }

    render() {
        const { groupId, children, hashtags } = this.props;

        let hashtagUrl = '/blogs/?h=';
        if (groupId) {
            hashtagUrl = `/group/${fromGlobalId(groupId).id}/?h=`;
        }

        const transformations = [escapeHtml, linkifyStr];
        if (hashtags) {
            transformations.push((str: string) => linkifyHashtags(str, hashtagUrl));
        }
        transformations.push(linkifyMentions, (str: string) => str.replace(/\n/g, '<br>'));

        const html = transformations.reduce(
            (result: string, transformation: (str: string) => string) => transformation(result),
            children as string
        ) as string;

        return map(html, this.onElement, this.onText);
    }
}

export default Styled(defaultStyles)(EnhancedText);
